import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from "recharts";
import { CalendarDays, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { api } from "@/lib/api";

export default function WeeklySummary() {
  const [data, setData] = useState(null);

  useEffect(() => { api.weeklySummary().then(setData).catch(() => {}); }, []);

  if (!data) return null;
  const hasAny = data.weeks.some((w) => w.count > 0);
  if (!hasAny) return null;

  return (
    <div data-testid="weekly-summary">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
        <CalendarDays className="h-3.5 w-3.5" /> Riepilogo settimanale
      </div>

      <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-sm space-y-5">
        <div className="flex items-baseline justify-between">
          <h3 className="font-display font-bold">Questa settimana</h3>
          <span className="text-sm text-muted-foreground">
            <span className="font-display font-extrabold text-lg text-foreground">{data.this_week_count}</span> quiz · media <span className="font-semibold text-foreground">{data.this_week_avg}%</span>
          </span>
        </div>

        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={data.weeks} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
            <Tooltip
              cursor={{ fill: "hsl(var(--muted))" }}
              contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))", fontSize: 13 }}
              formatter={(v, _n, p) => [`${v} quiz · ${p.payload.avg_pct}%`, "Settimana"]}
            />
            <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={48}>
              {data.weeks.map((w, i) => (
                <Cell key={i} fill={i === data.weeks.length - 1 ? "hsl(var(--primary))" : "hsl(var(--muted-foreground) / 0.35)"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {data.improvement.length > 0 && (
          <div className="space-y-2 pt-1">
            <div className="text-xs font-medium text-muted-foreground">Miglioramento per materia (vs settimana scorsa)</div>
            {data.improvement.map((im) => (
              <div key={im.subject_id} className="flex items-center justify-between text-sm" data-testid={`weekly-subject-${im.subject_id}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: im.color }} />
                  <span className="truncate">{im.name}</span>
                  <span className="text-xs text-muted-foreground shrink-0">({im.this_count} quiz)</span>
                </span>
                <DeltaBadge delta={im.delta} thisAvg={im.this_avg} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DeltaBadge({ delta, thisAvg }) {
  if (delta === null || delta === undefined) {
    return <span className="text-xs text-muted-foreground">{thisAvg != null ? `${thisAvg}%` : "—"} · nuovo</span>;
  }
  if (delta > 0) return <span className="inline-flex items-center gap-1 font-semibold text-emerald-600"><ArrowUp className="h-3.5 w-3.5" />+{delta}%</span>;
  if (delta < 0) return <span className="inline-flex items-center gap-1 font-semibold text-destructive"><ArrowDown className="h-3.5 w-3.5" />{delta}%</span>;
  return <span className="inline-flex items-center gap-1 font-semibold text-muted-foreground"><Minus className="h-3.5 w-3.5" />0%</span>;
}
